import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Undo2, AlertTriangle, Trash2, Calendar, User as UserIcon, RefreshCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin "Recovery" tab — lists every soft-deleted batch of daily_entries
 * from the last 30 days (and beyond — we don't auto-purge unless asked).
 * Each row shows what was deleted, by whom, when, and offers one-click
 * Restore or Permanently Delete buttons.
 *
 * Soft-delete is set by:
 *   - Layout "Clear This Page" → batch=1 doc
 *   - Layout "Clear All Data" → batch=N docs
 *   - Admin Users → Delete (user + their daily_entries)
 *   - Admin Data Tools → Shift Entries (pre-shift clear list)
 *   - POST /api/user/clear-all-data
 */
export const RecoveryTab = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [purging, setPurging] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/deleted-batches`);
      setBatches(res.data?.batches || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load deleted batches");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRestore = async (batch) => {
    if (!window.confirm(`Restore ${batch.count} entries for ${batch.target_user_email || '(unknown user)'}?\n\nDates: ${batch.min_date} → ${batch.max_date}\nDeleted: ${batch.deleted_at}`)) return;
    setBusyId(batch.batch_id);
    try {
      const res = await axios.post(`${API}/admin/restore-batch`, { batch_id: batch.batch_id });
      toast.success(`Restored ${res.data.restored} entries`);
      await load();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (typeof d === "object" && d.conflicts) {
        toast.error(`Cannot restore: ${d.conflicts.length} target dates already have data. Delete those first or restore manually.`);
      } else {
        toast.error(typeof d === "string" ? d : "Restore failed");
      }
    } finally {
      setBusyId(null);
    }
  };

  const handlePurgeBatch = async (batch) => {
    if (!window.confirm(`PERMANENTLY delete ${batch.count} entries?\n\nThis CANNOT be undone — the data will be gone forever.`)) return;
    setBusyId(batch.batch_id);
    try {
      const res = await axios.post(`${API}/admin/purge-batch`, { batch_id: batch.batch_id });
      toast.success(`Purged ${res.data.purged} entries permanently`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Purge failed");
    } finally {
      setBusyId(null);
    }
  };

  const handlePurgeOld = async () => {
    if (!window.confirm("Permanently delete ALL soft-deleted entries older than 30 days?\n\nThis cannot be undone.")) return;
    setPurging(true);
    try {
      const res = await axios.post(`${API}/admin/purge-old-deleted`);
      toast.success(`Purged ${res.data.purged} entries older than 30 days`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Purge-old failed");
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-recovery-tab">
      <div className="flex items-center gap-2">
        <Undo2 className="w-5 h-5 text-primary" />
        <h2 className="font-mono text-lg font-bold">Recovery — Soft-Deleted Entries</h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          className="ml-auto"
          data-testid="recovery-refresh"
        >
          <RefreshCcw className="w-4 h-4 mr-1" /> Refresh
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handlePurgeOld}
          disabled={purging}
          className="text-red-500 hover:text-red-400"
          data-testid="recovery-purge-old"
          title="Permanently delete entries older than 30 days"
        >
          <Trash2 className="w-4 h-4 mr-1" /> {purging ? 'Purging…' : 'Purge >30 days'}
        </Button>
      </div>
      <p className="font-mono text-xs text-card-foreground/80 -mt-2 max-w-3xl">
        Every &ldquo;Clear&rdquo; or &ldquo;Delete User&rdquo; action moves the data here instead of
        deleting it. You have <strong>30 days</strong> to one-click restore. After 30 days the
        button still works as long as the batch hasn&rsquo;t been hard-purged, but we recommend
        keeping the bin tidy.
      </p>

      {loading && <Card className="border-0"><CardContent className="p-6 text-center text-muted-foreground font-mono text-sm">Loading…</CardContent></Card>}

      {!loading && batches.length === 0 && (
        <Card className="border-0">
          <CardContent className="p-8 text-center text-muted-foreground font-mono text-sm" data-testid="recovery-empty">
            No soft-deleted batches. The bin is empty.
          </CardContent>
        </Card>
      )}

      <div className="space-y-3" data-testid="recovery-list">
        {batches.map((b) => (
          <Card key={b.batch_id} className="border-0 shadow-md" data-testid={`recovery-batch-${b.batch_id.slice(0,12)}`}>
            <CardHeader className="pb-2">
              <CardTitle className="font-mono text-sm flex items-start justify-between gap-3">
                <div className="flex flex-col gap-1">
                  <span className="text-base">{b.reason}</span>
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <UserIcon className="w-3 h-3" /> {b.target_user_email || '(unknown user)'}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {b.age_days < 30 ? (
                    <span className="text-[11px] px-2 py-1 bg-emerald-700/30 text-emerald-300 rounded font-mono">
                      {30 - b.age_days}d left to restore
                    </span>
                  ) : (
                    <span className="text-[11px] px-2 py-1 bg-amber-700/30 text-amber-300 rounded font-mono flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> {b.age_days}d old (past window)
                    </span>
                  )}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs font-mono">
                <div className="flex items-center gap-1 text-card-foreground/90">
                  <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                  <strong>{b.count}</strong> entr{b.count === 1 ? 'y' : 'ies'}
                </div>
                <div className="text-card-foreground/90">
                  Range: <strong>{b.min_date}</strong> → <strong>{b.max_date}</strong>
                </div>
                <div className="text-card-foreground/90">
                  Deleted by: <strong>{b.deleted_by_email || b.deleted_by_name || '(system)'}</strong>
                </div>
              </div>
              <div className="text-[11px] text-muted-foreground font-mono">
                Deleted at: {b.deleted_at} · Batch: <span className="opacity-60">{b.batch_id.slice(0, 16)}…</span>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => handleRestore(b)}
                  disabled={busyId === b.batch_id}
                  data-testid={`recovery-restore-${b.batch_id.slice(0,12)}`}
                >
                  <Undo2 className="w-3.5 h-3.5 mr-1" />
                  {busyId === b.batch_id ? 'Restoring…' : 'Restore'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePurgeBatch(b)}
                  disabled={busyId === b.batch_id}
                  className="text-red-500 hover:text-red-400"
                  data-testid={`recovery-purge-${b.batch_id.slice(0,12)}`}
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" />
                  Permanently Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default RecoveryTab;
